import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';
import Logo from '../../components/Logo/Logo';
import { trackPageView, trackButtonClick, trackNavigationClick } from '../../utils/analytics';
import {
  ArrowRight,
  Globe,
  Phone,
  Mail,
  Menu,
  X,
  BookOpen,
  CheckCircle,
  ShieldCheck,
  Zap,
  TrendingUp
} from 'lucide-react';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Interactive 3D tilt for the hero preview card (parallax toward the cursor).
  const [tilt, setTilt] = useState({ rx: 8, ry: -16 });
  const handleHeroMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: 8 - py * 14, ry: -16 + px * 18 });
  };
  const resetTilt = () => setTilt({ rx: 8, ry: -16 });

  // Track page view on component mount
  useEffect(() => {
    trackPageView('Landing Page', '/');
  }, []);


  const keyStats = [
    { number: '$85B', label: 'in government payments disbursed annually' },
    { number: '2.3B', label: 'customer service interactions supported each year' },
    { number: '$12.4B', label: 'in tolling transactions processed annually' }
  ];

  const featuredInsights = [
    {
      title: 'Navigating the New Era of Government Efficiency',
      type: 'White Paper',
      image: '/api/placeholder/300/200'
    },
    {
      title: 'Reimagining AI-driven employee experiences',
      type: 'eBook',
      image: '/api/placeholder/300/200'
    },
    {
      title: 'The future of claims automation',
      type: 'Blog Post',
      image: '/api/placeholder/300/200'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="PharmaChargeback — Chargebacks, validated and settled automatically"
        description="Ingest EDI 844 claims, validate them against your contracts and prices, adjudicate straight through, and reconcile — with a full audit trail end to end."
        keywords="pharmaceutical chargeback, EDI 844, EDI 849, contract management, chargeback validation, chargeback adjudication, gross-to-net, AR reconciliation, compliance, pharmaceutical software"
      />
      
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/" className="flex-shrink-0">
                <Logo variant="color" size="lg" />
              </Link>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <Link to="/login" className="text-gray-500 hover:text-gray-900">
                Sign In
              </Link>
              <Link to="/contact" className="text-gray-500 hover:text-gray-900">
                Contact
              </Link>
              <Link to="/about" className="text-gray-500 hover:text-gray-900">
                About
              </Link>
            </nav>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-700 hover:text-gray-900"
                title="Toggle menu"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="space-y-4">
                <Link to="/login" className="block text-gray-500 hover:text-gray-900 py-2">Sign In</Link>
                <Link to="/contact" className="block text-gray-500 hover:text-gray-900 py-2">Contact</Link>
                <Link to="/about" className="block text-gray-500 hover:text-gray-900 py-2">About</Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section — tasteful CSS 3D */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        {/* Drifting gradient blobs + faint grid */}
        <div className="pointer-events-none absolute inset-0">
          <div className="hero-blob" style={{ width: 520, height: 520, top: -120, left: -80, background: 'radial-gradient(circle, #2563eb, transparent 70%)' }} />
          <div className="hero-blob" style={{ width: 480, height: 480, top: 40, right: -100, background: 'radial-gradient(circle, #7c3aed, transparent 70%)', animationDelay: '4s' }} />
          <div className="hero-blob" style={{ width: 420, height: 420, bottom: -140, left: '40%', background: 'radial-gradient(circle, #06b6d4, transparent 70%)', animationDelay: '8s' }} />
          <div className="hero-grid" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            {/* Copy */}
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-sm text-blue-200 backdrop-blur">
                <ShieldCheck className="h-4 w-4" /> Pharmaceutical chargeback management
              </span>
              <h1 className="mt-6 text-4xl font-bold leading-tight md:text-6xl">
                Chargebacks, <span className="bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">validated and settled</span> — automatically.
              </h1>
              <p className="mt-6 max-w-xl text-lg text-blue-100/90 md:text-xl">
                Ingest EDI 844 claims, validate them against your contracts and prices, adjudicate straight through, and reconcile — with a full audit trail end to end.
              </p>
              <div className="mt-9 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/login"
                  onClick={() => trackButtonClick('Sign In - Hero', 'Hero Section')}
                  className="inline-flex items-center justify-center rounded-xl bg-white px-8 py-4 text-lg font-semibold text-blue-900 shadow-lg transition hover:bg-blue-50 hover:shadow-xl"
                >
                  Sign in <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/contact"
                  onClick={() => trackButtonClick('Contact Us - Hero', 'Hero Section')}
                  className="inline-flex items-center justify-center rounded-xl border border-white/25 px-8 py-4 text-lg font-semibold text-white transition hover:bg-white/10"
                >
                  Contact Us
                </Link>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-blue-200/80">
                <span className="inline-flex items-center gap-1.5"><Zap className="h-4 w-4" /> Straight-through adjudication</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle className="h-4 w-4" /> EDI 844 / 849 / 997</span>
                <span className="inline-flex items-center gap-1.5"><TrendingUp className="h-4 w-4" /> Gross-to-net &amp; AR</span>
              </div>
            </div>

            {/* Interactive 3D preview card */}
            <div className="hidden lg:block" style={{ perspective: '1400px' }} onMouseMove={handleHeroMove} onMouseLeave={resetTilt}>
              <div className="hero-card relative mx-auto max-w-md" style={{ transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)` }}>
                {/* soft glow behind the card */}
                <div className="absolute -inset-6 rounded-3xl bg-gradient-to-tr from-blue-500/30 to-cyan-400/20 blur-2xl" />
                <div className="hero-card-layer relative rounded-2xl border border-white/15 bg-white/95 p-5 text-gray-800 shadow-2xl">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                    <div className="flex items-center gap-2">
                      <Logo variant="icon" size="sm" />
                      <span className="text-sm font-semibold text-gray-900">Chargeback CB-2026-0481</span>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700"><CheckCircle className="h-3 w-3" /> Approved</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 py-4">
                    <div className="rounded-lg bg-gray-50 p-3">
                      <div className="text-xs text-gray-400">Customer</div>
                      <div className="text-sm font-medium">Cardinal Health #30</div>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3">
                      <div className="text-xs text-gray-400">Total</div>
                      <div className="text-sm font-semibold text-gray-900">$44,054.09</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[['Amoxicillin 500mg', '$246.98', 'ok'], ['Albuterol 90mcg', '$59.43', 'ok'], ['Gabapentin 300mg', '$269.40', 'ok']].map(([name, price]) => (
                      <div key={name} className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2 text-sm">
                        <span className="text-gray-700">{name}</span>
                        <span className="flex items-center gap-2 font-medium text-gray-900">{price}<CheckCircle className="h-4 w-4 text-green-500" /></span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center justify-between rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white">
                    <span className="flex items-center gap-2"><Zap className="h-4 w-4" /> Auto-adjudicated</span>
                    <span>Within tolerance</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Stats Section */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Delivering for our clients at scale
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {keyStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl md:text-6xl font-bold text-blue-600 mb-4">
                  {stat.number}
                </div>
                <div className="text-lg text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Featured Insights */}
      <section className="bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Featured insights
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredInsights.map((insight, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="h-48 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                  <BookOpen className="h-16 w-16 text-blue-600" />
                </div>
                <div className="p-6">
                  <div className="text-sm text-blue-600 font-semibold mb-2">{insight.type}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{insight.title}</h3>
                  <Link 
                    to="/insights" 
                    className="text-blue-600 font-semibold hover:text-blue-700 inline-flex items-center"
                  >
                    Learn more
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Move your organization ahead
          </h2>
          <p className="text-xl text-blue-100 mb-12 max-w-4xl mx-auto">
            Every day PharmaChargeback helps improve processes, interactions and outcomes for our clients and the millions of people who count on them.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/login"
              onClick={() => trackButtonClick('Get Started - CTA', 'CTA Section')}
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors inline-flex items-center justify-center"
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/contact"
              onClick={() => trackButtonClick('Contact Sales - CTA', 'CTA Section')}
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600 transition-colors inline-flex items-center justify-center"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <Logo variant="color" size="lg" className="mb-6" />
              <p className="text-gray-400 mb-6">
                Technology-led solutions to improve experiences, drive efficiency and reduce costs for pharmaceutical companies.
              </p>
              <div className="flex space-x-4">
                <Globe className="h-5 w-5 text-gray-400 hover:text-white cursor-pointer" />
                <Phone className="h-5 w-5 text-gray-400 hover:text-white cursor-pointer" />
                <Mail className="h-5 w-5 text-gray-400 hover:text-white cursor-pointer" />
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/features" className="text-gray-400 hover:text-white">Features</Link></li>
                <li><Link to="/demo" className="text-gray-400 hover:text-white">Demo</Link></li>
                <li><Link to="/api" className="text-gray-400 hover:text-white">API</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white">About Us</Link></li>
                <li><Link to="/press" className="text-gray-400 hover:text-white">Press</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><Link to="/contact" className="text-gray-400 hover:text-white">Contact Us</Link></li>
                <li><Link to="/privacy" className="text-gray-400 hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 PharmaChargeback. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
